import React, { ReactNode, useState } from 'react';
import { JBAlert, JBTypography, JBButton } from '@jbhi-fi/lanyard-ui';
import JBAccordion from '@jbhi-fi/lanyard-ui/components/Accordion';
import JBCheckBox from '@jbhi-fi/lanyard-ui/components/Checkbox';
import UnmountAtBreakpoint from '../../../../common/UnmountAtBreakpoint';
import { PerksBenefit } from '../../../../perks/components/perksLanding/perksBenefit/PerksBenefit';
import PerksMascotIllustrated from '@jbhi-fi/lanyard-ui/components/Icons/PerksMascotIllustrated';
import IconPlusSvg from '../../../../../../../assets/svg/icon-plus.svg';
import IconMinusSvg from '../../../../../../../assets/svg/icon-minus.svg';
import IconPerksCake from '../../../../../../../assets/svg/icon-perks-cake.svg';
import IconPerksMegaphone from '../../../../../../../assets/svg/icon-perks-megaphone.svg';
import IconPerksAlerts from '../../../../../../../assets/svg/icon-perks-alerts.svg';
import IconPerksDiscounts from '../../../../../../../assets/svg/icon-perks-discounts.svg';
import { handleLoginCreate } from '../../../../../../utils/signInOutUp/AuthenticationHelper';
import CustomerHelper from '../../../helpers/CustomerHelper';
import { styles } from './PerksPrompt.css';
import { Tracker } from '@jbhi-fi/components/src/Tracking';

const customerHelper = new CustomerHelper();

const CHECKBOX_ERROR_MESSAGE = 'Please accept the terms and conditions to continue.';
const DEFAULT_PERKS_HEADING = 'Become a JB Perks member for an instant $10 coupon and more!';
type PerksPromptProps = {
  heading?: string;
  storeId?: number;
  variant?: 'small' | 'full';
  onClose?: () => void;
  onSkipJoinPerks?: () => Promise<boolean>;
};

const PerksLabel = () => {
  return (
    <>
      By becoming a JB Hi-Fi Perks member, I agree to the{' '}
      <a target="_blank" href="/perksterms" className={styles.contentLink}>
        JB Hi-Fi Perks terms and conditions
      </a>
      ,{' '}
      <a target="_blank" href="/privacypolicy" className={styles.contentLink}>
        Privacy policy
      </a>{' '}
      and being subscribed to marketing communications (which I can unsubscribe from at any time).{' '}
    </>
  );
};
export const PerksPrompt = ({
  heading,
  storeId,
  variant = 'full',
  onSkipJoinPerks,
  onClose,
}: PerksPromptProps): JSX.Element => {
  const [hasAcceptedTerms, setHasAcceptedTerms] = useState(false);
  const [termsPolicyErrorDisplayed, setTermsPolicyErrorDisplayed] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [isPreferenceSaving, setIsPreferenceSaving] = useState<boolean>(false);
  const [preferenceSaveError, setPreferenceSaveError] = useState<boolean>(false);
  const returnUrl = `/account/login?pageTheme=perks&source=DRExist${storeId ? `&storeId=${storeId}` : ''}`;

  const accordionItems = [
    {
      urlHashId: 'perks-grid',
      head: (
        <JBTypography type="h5" margin="0">
          What else you get
        </JBTypography>
      ),
      content: <BenefitList />,
      closeIcon: <IconMinusSvg />,
      openIcon: <IconPlusSvg />,
    },
  ];

  const handleTrackClickCta = (label: string, url?: string) => {
    const event = {
      event: 'click_cta',
      link_text: label,
      location: 'header',
      ...(url && { link_url: url }),
    };

    Tracker.trackCustomEvent(event);
  };

  const handleJoinPerksClick = () => {
    if (variant === 'full' && !hasAcceptedTerms) return setTermsPolicyErrorDisplayed(CHECKBOX_ERROR_MESSAGE);
    if (isSubmitting) return;

    setIsSubmitting(true);

    handleLoginCreate(returnUrl, {
      pageTheme: 'perks',
    });
    handleTrackClickCta('Join JB Perks', returnUrl);
    setIsSubmitting(false);
  };

  const savePerksPreference = async (): Promise<boolean> => {
    try {
      const { error, isSuccess } = await customerHelper.setCustomerPreferences(window.Jbhifi.shopifyCustomerId, {
        perks_skipJoinPrompt: 'true',
      });
      if (error || !isSuccess) throw new Error('Error saving perks preferences');
      return true;
    } catch (error) {
      return false;
    }
  };

  const handleSavePreference = async () => {
    setIsPreferenceSaving(true);
    setPreferenceSaveError(false);
    let isSaveSuccess = false;
    try {
      if (onSkipJoinPerks) {
        isSaveSuccess = await onSkipJoinPerks();
      } else {
        isSaveSuccess = await savePerksPreference();
      }
      setPreferenceSaveError(!isSaveSuccess);
    } catch (error) {
      setPreferenceSaveError(true);
    } finally {
      setIsPreferenceSaving(false);
      if (isSaveSuccess && onClose) {
        onClose();
      }
    }

    handleTrackClickCta('Skip joining Perks');
  };

  const handleAcceptTermsChange = () => {
    setHasAcceptedTerms(!hasAcceptedTerms);
    setTermsPolicyErrorDisplayed(null);
  };

  return (
    <>
      {preferenceSaveError && variant === 'full' ? (
        <JBAlert showIcon type="warning" heading="There was a problem saving your preference. Please try again." />
      ) : null}
      <div className={styles.rootContainer({ variant })}>
        <div className={styles.mascotContainer({ variant })}>
          <PerksMascotIllustrated />
        </div>
        <div className={styles.textContainer}>
          <h3 className={styles.heading({ variant })}>{heading || DEFAULT_PERKS_HEADING}</h3>
          {variant === 'small' ? null : (
            <>
              <UnmountAtBreakpoint breakpoint="md" matchRule="up">
                <JBAccordion
                  id="perks-prompt-accordion"
                  defaultActiveIndex={null}
                  items={accordionItems}
                  className={styles.accordion}
                  accordionItemClassname={styles.accordionItem}
                  accordionHeaderClassname={styles.accordionHeader}
                  accordionContentClassname={styles.accordionContent}
                  multiOpen
                />
              </UnmountAtBreakpoint>
              <UnmountAtBreakpoint breakpoint="sm" matchRule="down">
                <BenefitList />
              </UnmountAtBreakpoint>
              <JBCheckBox
                id="accept-terms"
                name="accept-terms"
                value="accepted"
                checked={hasAcceptedTerms}
                onChange={handleAcceptTermsChange}
                label={<PerksLabel />}
              />
              {termsPolicyErrorDisplayed ? <p className={styles.policyError}>{termsPolicyErrorDisplayed}</p> : null}
            </>
          )}

          <div className={styles.actionContainer({ variant: variant })}>
            <JBButton
              type="primary"
              label={isSubmitting ? 'Joining...' : 'Join JB Perks'}
              classes={styles.joinButton({ variant: variant })}
              onClick={handleJoinPerksClick}
            />
            <JBButton
              type="link"
              label={isPreferenceSaving ? 'Saving...' : 'No thanks'}
              onClick={handleSavePreference}
            />
          </div>
        </div>
      </div>
    </>
  );
};

const BenefitList = () => {
  return (
    <div className={styles.benefitGrid}>
      <PerksBenefit
        heading="$10 JB welcome coupon"
        content="Get a $10 Perks coupon just for joining."
        icon={<IconPerksAlerts />}
      />
      <PerksBenefit
        heading="Exclusive member discounts"
        content="Get it for less with Perks member only discount deals."
        icon={<IconPerksDiscounts />}
      />
      <PerksBenefit
        heading="Early sales notifications"
        content="You'll be the first to know when our biggest and best sales are happening."
        icon={<IconPerksMegaphone style={{ transform: 'rotate(-18.9deg)' }} />}
      />
      <PerksBenefit
        heading="Birthday Perk"
        content="If you tell us when it's your birthday, we'll send you a gift to help celebrate."
        icon={<IconPerksCake />}
      />
    </div>
  );
};
